<template>
  <div class="consult-workbench">
    <div class="page-head">
      <div>
        <h2>咨询管理</h2>
        <p>左侧选择小程序用户咨询，右侧像聊天一样回复用户消息。</p>
      </div>
      <el-button size="small" icon="el-icon-refresh" @click="refreshCurrent">刷新</el-button>
    </div>

    <div class="workbench-card">
      <aside class="session-pane">
        <div class="session-filter">
          <el-input
            v-model="filters.keyword"
            size="small"
            prefix-icon="el-icon-search"
            placeholder="客户编号 / 咨询编号 / 用户 / 内容"
            clearable
            @keyup.enter.native="search"
            @clear="search"
          />
          <el-select v-model="filters.status" size="small" placeholder="全部状态" clearable @change="search">
            <el-option label="待处理" value="pending" />
            <el-option label="跟进中" value="following" />
            <el-option label="已关闭" value="closed" />
            <el-option label="已取消" value="cancelled" />
          </el-select>
        </div>

        <div v-loading="loading" class="session-list">
          <button
            v-for="item in rows"
            :key="item.id"
            type="button"
            class="session-item"
            :class="{ active: selected && selected.id === item.id }"
            @click="selectSession(item)"
          >
            <div class="session-avatar-wrap">
              <div class="session-avatar">{{ avatarText(item) }}</div>
              <span v-if="item.unread_count" class="session-unread">{{ unreadText(item.unread_count) }}</span>
            </div>
            <div class="session-main">
              <div class="session-top">
                <span class="session-name">{{ item.nickname || item.name || '小程序用户' }}</span>
                <span class="session-time">{{ formatTime(item.updated_at || item.created_at) }}</span>
              </div>
              <div class="session-source">
                {{ consultationTypeText(item) }}
                <span v-if="item.consultation_count > 1"> · {{ item.consultation_count }} 个咨询事项</span>
              </div>
              <div class="session-code">{{ item.customer_no || '-' }} · 最近咨询 {{ item.consultation_no || '-' }}</div>
              <div class="session-preview">{{ messagePreview(item) }}</div>
            </div>
            <span class="session-status" :class="`status-${item.status || 'pending'}`">{{ statusText(item.status) }}</span>
          </button>

          <div v-if="!loading && !rows.length" class="session-empty">暂无咨询会话</div>
        </div>

        <div class="session-pager">
          <el-pagination
            small
            layout="prev, pager, next"
            :current-page="page"
            :page-size="pageSize"
            :total="total"
            @current-change="page = $event; fetchList(false)"
          />
        </div>
      </aside>

      <main class="chat-pane">
        <template v-if="selected">
          <header class="chat-header">
            <div>
              <div class="chat-title">{{ selected.nickname || selected.name || '小程序用户' }}</div>
              <div class="chat-subtitle">
                <span>{{ consultationTypeText(selectedDetail.source_type ? selectedDetail : selected) }}</span>
                <span v-if="selectedDetail.phone">手机号：{{ selectedDetail.phone }}</span>
                <span>客户编号：{{ selectedDetail.customer_no || selected.customer_no || '-' }}</span>
                <span>咨询编号：{{ selectedDetail.consultation_no || selected.consultation_no || '-' }}</span>
              </div>
            </div>
            <div class="chat-actions">
              <el-select v-model="selected.status" size="small" class="status-select" @change="saveStatus">
                <el-option label="待处理" value="pending" />
                <el-option label="跟进中" value="following" />
                <el-option label="已关闭" value="closed" />
                <el-option label="已取消" value="cancelled" />
              </el-select>
              <el-button size="small" @click="fetchMessages(selected.id)">刷新消息</el-button>
            </div>
          </header>

          <section v-if="selectedDetail.demand_context" class="demand-context-card">
            <div class="demand-context-title">本次定制需求</div>
            <div class="demand-context-grid">
              <span>出行日期：{{ String(selectedDetail.demand_context.travel_date || '待确认').slice(0, 10) }}</span>
              <span>出行人数：成人 {{ Number(selectedDetail.demand_context.adult_count || 0) }}、儿童 {{ Number(selectedDetail.demand_context.child_count || 0) }}、老人 {{ Number(selectedDetail.demand_context.elder_count || 0) }}</span>
              <span v-if="selectedDetail.demand_context.must_visit">想去地点：{{ selectedDetail.demand_context.must_visit }}</span>
              <span v-if="selectedDetail.demand_context.hotel_level">酒店标准：{{ selectedDetail.demand_context.hotel_level }}</span>
              <span v-if="selectedDetail.demand_context.needs && selectedDetail.demand_context.needs.length">服务偏好：{{ selectedDetail.demand_context.needs.join('、') }}</span>
              <span v-if="selectedDetail.demand_context.extra_note">补充需求：{{ selectedDetail.demand_context.extra_note }}</span>
            </div>
          </section>

          <section ref="messageList" v-loading="messageLoading" class="message-list">
            <div v-if="!messageLoading && !messages.length" class="message-empty">暂无聊天消息</div>
            <div
              v-for="message in messages"
              :key="message.id"
              class="message-row"
              :class="{ mine: message.sender_type === 'admin' }"
            >
              <div class="message-avatar">{{ message.sender_type === 'admin' ? '管' : avatarText(selected) }}</div>
              <div class="message-body">
                <div class="message-name">
                  {{ message.sender_type === 'admin' ? (message.admin_name || '后台客服') : customerDisplayName }}
                </div>
                <div v-if="message.consultation_name" class="message-context">{{ message.consultation_name }}</div>
                <div v-if="message.message_type === 'image'" class="message-image-bubble">
                  <el-image
                    :src="messageImageUrl(message.content)"
                    :preview-src-list="[messageImageUrl(message.content)]"
                    fit="cover"
                  >
                    <div slot="error" class="message-image-error">图片加载失败</div>
                  </el-image>
                </div>
                <div v-else class="message-bubble">{{ message.content }}</div>
                <div class="message-time">{{ message.created_at | dateTime }}</div>
              </div>
            </div>
          </section>

          <section class="session-meta">
            <el-input
              v-model="selected.remark"
              size="small"
              type="textarea"
              :rows="2"
              placeholder="内部备注，用户不可见"
              @blur="saveStatus"
            />
          </section>

          <footer class="reply-bar">
            <div class="reply-tools">
              <el-popover placement="top-start" width="360" trigger="click">
                <div class="quick-reply-list"><button v-for="item in activeQuickReplies" :key="item.id" type="button" class="quick-reply-item" @click="useQuickReply(item)"><strong>{{item.title}}</strong><span>{{item.content}}</span></button><div v-if="!activeQuickReplies.length" class="quick-reply-empty">暂无启用的话术</div></div>
                <el-button slot="reference" size="small" icon="el-icon-chat-line-square">常用话术</el-button>
              </el-popover>
              <el-button size="small" icon="el-icon-setting" @click="quickReplyVisible=true">管理话术</el-button>
              <el-upload
                action="#"
                accept="image/*"
                :show-file-list="false"
                :http-request="sendImage"
                :before-upload="beforeImageUpload"
                :disabled="imageSending || replying"
              >
                <el-button size="small" icon="el-icon-picture-outline" :loading="imageSending">发送图片</el-button>
              </el-upload>
              <span>支持 JPG、PNG、GIF、WebP 等图片，单张不超过 10MB</span>
            </div>
            <div class="reply-compose">
              <el-input
                v-model="replyContent"
                type="textarea"
                :rows="3"
                resize="none"
                placeholder="输入回复内容，发送后用户会在小程序聊天页看到"
                @keydown.native.exact.enter.prevent="sendReply"
              />
              <el-button type="primary" :loading="replying" :disabled="imageSending" @click="sendReply">发送</el-button>
            </div>
          </footer>
        </template>

        <div v-else class="chat-placeholder">
          <i class="el-icon-chat-dot-round"></i>
          <span>请选择左侧咨询会话</span>
        </div>
      </main>
    </div>
    <el-dialog title="常用话术管理" :visible.sync="quickReplyVisible" width="680px" append-to-body><el-button size="small" type="primary" @click="editQuickReply()">新增话术</el-button><el-table :data="quickReplies" size="mini" border><el-table-column prop="title" label="标题" width="130"/><el-table-column prop="content" label="内容" min-width="260" show-overflow-tooltip/><el-table-column prop="status" label="状态" width="80"><template slot-scope="{row}">{{row.status==='active'?'启用':'停用'}}</template></el-table-column><el-table-column label="操作" width="120"><template slot-scope="{row}"><el-button type="text" @click="editQuickReply(row)">编辑</el-button><el-button type="text" class="danger-text" @click="removeQuickReply(row)">删除</el-button></template></el-table-column></el-table></el-dialog>
    <el-dialog title="编辑常用话术" :visible.sync="quickReplyEditVisible" width="520px" append-to-body><el-form :model="quickReplyForm" label-width="80px"><el-form-item label="标题"><el-input v-model="quickReplyForm.title" maxlength="80"/></el-form-item><el-form-item label="内容"><el-input v-model="quickReplyForm.content" type="textarea" :rows="6" maxlength="5000" show-word-limit/></el-form-item><el-form-item label="状态"><el-switch v-model="quickReplyForm.active" active-text="启用"/></el-form-item><el-form-item label="排序"><el-input-number v-model="quickReplyForm.sort_order" :min="0"/></el-form-item></el-form><span slot="footer"><el-button @click="quickReplyEditVisible=false">取消</el-button><el-button type="primary" @click="saveQuickReply">保存</el-button></span></el-dialog>
  </div>
</template>

<script>
import { listResource } from "../api/resources";
import http from "../api/http";
import { apiOrigin } from "../api/config";
import { subscribe } from "../utils/realtime";

const statusMap = {
  pending: "待处理",
  following: "跟进中",
  processing: "跟进中",
  closed: "已关闭",
  cancelled: "已取消"
};

export default {
  name: "ConsultationsManage",
  data() {
    return {
      loading: false,
      messageLoading: false,
      replying: false,
      imageSending: false,
      rows: [],
      total: 0,
      page: 1,
      pageSize: 20,
      filters: { keyword: "", status: "" },
      selected: null,
      selectedDetail: {},
      messages: [],
      replyContent: "",
      quickReplies: [], quickReplyVisible: false, quickReplyEditVisible: false, quickReplyForm: {}
    };
  },
  mounted() {
    this.unsubscribeRealtime = subscribe(this.handleRealtimeEvent);
    this.fetchList(true);
    this.fetchQuickReplies();
  },
  beforeDestroy() {
    if (this.unsubscribeRealtime) this.unsubscribeRealtime();
    if (this.realtimeRefreshTimer) window.clearTimeout(this.realtimeRefreshTimer);
  },
  computed: {
    activeQuickReplies() { return this.quickReplies.filter((item) => item.status === "active"); },
    customerDisplayName() {
      return this.selectedDetail.nickname || (this.selected && this.selected.nickname) || "小程序用户";
    }
  },
  methods: {
    consultationTypeText(item = {}) {
      if (item.source_type === "custom_demand") return "高端定制需求";
      return item.source_page || item.name || item.source_type || "小程序在线咨询";
    },
    async fetchQuickReplies(){const res=await http.get("/consultations/quick-replies");this.quickReplies=res.data||[];},
    useQuickReply(item){this.replyContent=this.replyContent?`${this.replyContent}\n${item.content}`:item.content;},
    editQuickReply(row=null){this.quickReplyForm=row?{...row,active:row.status==="active"}:{title:"",content:"",active:true,sort_order:this.quickReplies.length};this.quickReplyEditVisible=true;},
    async saveQuickReply(){const data={...this.quickReplyForm,status:this.quickReplyForm.active?"active":"inactive"};if(data.id)await http.put(`/consultations/quick-replies/${data.id}`,data);else await http.post("/consultations/quick-replies",data);this.quickReplyEditVisible=false;await this.fetchQuickReplies();this.$message.success("常用话术已保存");},
    async removeQuickReply(row){await this.$confirm(`确认删除话术“${row.title}”？`,"提示",{type:"warning"});await http.delete(`/consultations/quick-replies/${row.id}`);await this.fetchQuickReplies();},
    unreadText(count) {
      const value = Number(count || 0);
      return value > 99 ? "99+" : String(value);
    },
    handleRealtimeEvent(payload) {
      if (!payload || !["consultation.message", "consultation.read"].includes(payload.event)) return;
      if (this.realtimeRefreshTimer) window.clearTimeout(this.realtimeRefreshTimer);
      this.realtimeRefreshTimer = window.setTimeout(async () => {
        this.realtimeRefreshTimer = null;
        const threadId = Number(payload.data && payload.data.thread_id);
        await this.fetchList(false);
        if (this.selected && Number(this.selected.id) === threadId && payload.event === "consultation.message") {
          await this.fetchMessages(this.selected.id);
        }
      }, 80);
    },
    statusText(status) {
      return statusMap[status] || "待处理";
    },
    avatarText(item) {
      const name = (item && (item.name || item.nickname || item.phone)) || "客";
      return String(name).slice(0, 1);
    },
    formatTime(value) {
      return this.$formatShortDateTime(value, "");
    },
    messagePreview(item) {
      if (item && item.last_message_type === "image") return "[图片]";
      return (item && (item.last_message || item.content)) || "暂无消息内容";
    },
    messageImageUrl(value) {
      if (!value || /^https?:\/\//i.test(value)) return value || "";
      return value.indexOf("/uploads/") === 0 ? `${apiOrigin()}${value}` : value;
    },
    search() {
      this.page = 1;
      this.fetchList(true);
    },
    async fetchList(autoSelect) {
      this.loading = true;
      try {
        const res = await listResource("consultations", { ...this.filters, page: this.page, pageSize: this.pageSize });
        this.rows = res.data && res.data.items ? res.data.items : [];
        this.total = res.data && res.data.total ? res.data.total : 0;
        if (autoSelect && this.rows.length) {
          const current = this.selected && this.rows.find((item) => item.id === this.selected.id);
          this.selectSession(current || this.rows[0]);
        }
        if (!this.rows.length) {
          this.selected = null;
          this.selectedDetail = {};
          this.messages = [];
        }
      } finally {
        this.loading = false;
      }
    },
    async selectSession(item) {
      if (!item) return;
      this.selected = { ...item };
      this.selectedDetail = {};
      this.replyContent = "";
      await this.fetchMessages(item.id);
    },
    async fetchMessages(id) {
      if (!id) return;
      this.messageLoading = true;
      try {
        const res = await http.get(`/consultations/threads/${id}/messages`, { showErrorMessage: false });
        this.selectedDetail = res.data && res.data.consultation ? res.data.consultation : {};
        this.messages = res.data && res.data.messages ? res.data.messages : [];
        const row = this.rows.find((item) => Number(item.id) === Number(id));
        if (row) row.unread_count = 0;
        this.$nextTick(this.scrollToBottom);
      } finally {
        this.messageLoading = false;
      }
    },
    scrollToBottom() {
      const list = this.$refs.messageList;
      if (list) list.scrollTop = list.scrollHeight;
    },
    async sendReply() {
      const content = String(this.replyContent || "").trim();
      if (!this.selected || !this.selected.id) return;
      if (!content) {
        this.$message.warning("请输入回复内容");
        return;
      }
      this.replying = true;
      try {
        await http.post(`/consultations/threads/${this.selected.id}/messages`, { content });
        this.replyContent = "";
        if (this.selected.status !== "closed") this.selected.status = "following";
        await this.fetchMessages(this.selected.id);
        await this.fetchList(false);
        this.$message.success("回复已发送");
      } finally {
        this.replying = false;
      }
    },
    beforeImageUpload(file) {
      const supportedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
      const isImage = Boolean(file && file.type && supportedTypes.includes(file.type.toLowerCase()));
      const withinLimit = Boolean(file && file.size <= 10 * 1024 * 1024);
      if (!isImage) this.$message.error("请选择 JPG、PNG、GIF 或 WebP 图片");
      else if (!withinLimit) this.$message.error("图片不能超过 10MB");
      return isImage && withinLimit;
    },
    async sendImage(options) {
      if (!this.selected || !this.selected.id || !options || !options.file) return;
      const form = new FormData();
      form.append("file", options.file);
      this.imageSending = true;
      try {
        await http.post(`/consultations/threads/${this.selected.id}/images`, form, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000
        });
        if (this.selected.status !== "closed") this.selected.status = "following";
        await this.fetchMessages(this.selected.id);
        await this.fetchList(false);
        if (options.onSuccess) options.onSuccess();
        this.$message.success("图片已发送");
      } catch (error) {
        if (options.onError) options.onError(error);
      } finally {
        this.imageSending = false;
      }
    },
    async saveStatus() {
      if (!this.selected || !this.selected.id) return;
      await http.put(`/consultations/threads/${this.selected.id}`, {
        status: this.selected.status,
        remark: this.selected.remark
      });
      const row = this.rows.find((item) => item.id === this.selected.id);
      if (row) Object.assign(row, this.selected);
    },
    async refreshCurrent() {
      await this.fetchList(false);
      if (this.selected && this.selected.id) await this.fetchMessages(this.selected.id);
    }
  }
};
</script>

<style scoped>
.consult-workbench {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 112px);
  height: calc(100dvh - 112px);
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.workbench-card {
  flex: 1 1 auto;
  display: grid;
  grid-template-columns: 360px minmax(0, 1fr);
  min-height: 0;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e6eaf2;
  border-radius: 8px;
}

.session-pane {
  display: flex;
  flex-direction: column;
  min-width: 0;
  border-right: 1px solid #e6eaf2;
  background: #f7f9fc;
}

.session-filter {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  padding: 14px;
  border-bottom: 1px solid #e6eaf2;
}

.session-list {
  flex: 1;
  min-height: 0;
  overflow-y: scroll;
  scrollbar-gutter: stable;
  padding: 8px;
}

.session-item {
  position: relative;
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  gap: 10px;
  width: 100%;
  margin-bottom: 8px;
  padding: 12px 10px;
  text-align: left;
  background: #fff;
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: pointer;
}

.session-item.active {
  border-color: #0b55c6;
  box-shadow: 0 8px 20px rgba(11, 85, 198, 0.1);
}

.session-avatar,
.message-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: #0b55c6;
  border-radius: 50%;
  font-weight: 700;
}

.session-avatar {
  width: 42px;
  height: 42px;
}

.session-avatar-wrap {
  position: relative;
  width: 42px;
  height: 42px;
}

.session-unread {
  position: absolute;
  top: -6px;
  right: -8px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  color: #fff;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
  background: #f56c6c;
  border: 2px solid #fff;
  border-radius: 11px;
  box-sizing: content-box;
}

.session-main {
  min-width: 0;
}

.session-top {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.session-name {
  overflow: hidden;
  color: #20242b;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.session-time,
.session-source,
.session-code,
.session-preview {
  color: #7b8494;
  font-size: 12px;
}

.session-source,
.session-code,
.session-preview {
  overflow: hidden;
  margin-top: 5px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.session-status {
  position: absolute;
  right: 10px;
  bottom: 10px;
  padding: 2px 6px;
  border-radius: 999px;
  font-size: 12px;
}

.status-pending {
  color: #b7791f;
  background: #fff7e6;
}

.status-following,
.status-processing {
  color: #0b55c6;
  background: #eaf3ff;
}

.status-closed {
  color: #606266;
  background: #f0f2f5;
}

.status-cancelled {
  color: #c23b3b;
  background: #fff0f0;
}

.session-empty,
.message-empty,
.chat-placeholder {
  color: #909399;
  text-align: center;
}

.session-empty {
  padding: 40px 0;
}

.session-pager {
  padding: 10px;
  text-align: center;
  border-top: 1px solid #e6eaf2;
}

.chat-pane {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  background: #fff;
  overflow: hidden;
}

.chat-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  border-bottom: 1px solid #e6eaf2;
}

.chat-title {
  color: #20242b;
  font-size: 18px;
  font-weight: 700;
}

.demand-context-card {
  margin: 12px 18px 0;
  padding: 12px 14px;
  color: #5f6671;
  background: #fffaf0;
  border: 1px solid #ead8b5;
  border-radius: 8px;
}

.demand-context-title {
  color: #8a6227;
  font-weight: 700;
}

.demand-context-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.5;
}

.chat-subtitle {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 6px;
  color: #7b8494;
  font-size: 12px;
}

.chat-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.status-select {
  width: 110px;
}

.message-list {
  flex: 1;
  min-height: 0;
  overflow-y: scroll;
  scrollbar-gutter: stable;
  padding: 18px;
  background: #f6f8fb;
}

.message-empty {
  padding-top: 120px;
}

.message-row {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
}

.message-row.mine {
  flex-direction: row-reverse;
}

.message-avatar {
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  background: #c7a35a;
}

.message-row.mine .message-avatar {
  background: #0b55c6;
}

.message-body {
  max-width: calc(100% - 48px);
  min-width: 0;
}

.message-row.mine .message-body {
  text-align: right;
}

.message-name,
.message-time {
  color: #909399;
  font-size: 12px;
}
.message-context{display:inline-block;margin-top:4px;padding:2px 7px;color:#8a6227;background:#fff7e6;border:1px solid #ead8b5;border-radius:999px;font-size:11px}

.message-bubble {
  display: inline-block;
  margin: 4px 0;
  padding: 10px 12px;
  color: #303133;
  background: #fff;
  border: 1px solid #e6eaf2;
  border-radius: 8px;
  line-height: 1.55;
  text-align: left;
  white-space: pre-wrap;
  word-break: break-word;
  max-width: 100%;
  box-sizing: border-box;
  overflow-wrap: anywhere;
}

.message-row.mine .message-bubble {
  color: #fff;
  background: #0b55c6;
  border-color: #0b55c6;
}

.message-image-bubble {
  width: 220px;
  margin: 4px 0;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e6eaf2;
  border-radius: 10px;
  line-height: 0;
}

.message-image-bubble ::v-deep .el-image {
  display: block;
  width: 220px;
  max-height: 260px;
}

.message-image-bubble ::v-deep img {
  max-height: 260px;
  object-fit: cover;
}

.message-image-error {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
}

.session-meta {
  flex: 0 0 auto;
  padding: 10px 18px;
  border-top: 1px solid #e6eaf2;
}

.reply-bar {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 18px 18px;
  border-top: 1px solid #e6eaf2;
}
.quick-reply-list{max-height:320px;overflow-y:auto}.quick-reply-item{display:block;width:100%;padding:10px;text-align:left;background:#fff;border:0;border-bottom:1px solid #ebeef5;cursor:pointer}.quick-reply-item:hover{background:#f5f7fa}.quick-reply-item strong,.quick-reply-item span{display:block}.quick-reply-item span{margin-top:5px;overflow:hidden;color:#737985;font-size:12px;line-height:1.5;text-overflow:ellipsis;white-space:nowrap}.quick-reply-empty{padding:24px;color:#909399;text-align:center}

.reply-tools {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #909399;
  font-size: 12px;
}

.reply-compose {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 96px;
  gap: 12px;
  align-items: end;
}

.chat-placeholder {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 14px;
}

.chat-placeholder i {
  font-size: 42px;
}

@media (max-width: 1200px) {
  .consult-workbench {
    height: calc(100vh - 96px);
    height: calc(100dvh - 96px);
  }

  .workbench-card {
    grid-template-columns: 300px minmax(0, 1fr);
  }
}

@media (max-width: 1000px) {
  .consult-workbench {
    height: auto;
    min-height: calc(100vh - 96px);
    min-height: calc(100dvh - 96px);
  }

  .workbench-card {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: minmax(260px, 38vh) minmax(520px, auto);
    height: auto;
    min-height: 0;
    overflow: visible;
  }

  .session-pane {
    border-right: 0;
    border-bottom: 1px solid #e6eaf2;
  }

  .chat-pane {
    min-height: 520px;
  }

  .chat-header {
    flex-wrap: wrap;
  }

  .reply-tools {
    flex-wrap: wrap;
  }
}

@media (max-width: 700px) {
  .chat-header {
    flex-direction: column;
  }

  .chat-actions {
    width: 100%;
  }

  .status-select {
    flex: 1 1 auto;
    width: auto;
  }

  .reply-compose {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
